import {
  AWTEventPriority,
  AWTEventProperties,
  AWTLogger,
  AWTLogManager
} from '@aria/webjs-sdk';
import { EventType, LogLevel } from './Telemetry.constants';
import { ITelemetryContext, UserBIEvent } from './Telemetry.types';
import { ITelemetryClient } from './telemetryClient.types';
import { IScenarioEvent } from './TelemetryScenarioTracker.types';

export class AriaTelemetryClient implements ITelemetryClient {
  public awtLogger: AWTLogger;

  constructor(ingestionKey: string) {
    AWTLogManager.initialize(ingestionKey);
    this.awtLogger = AWTLogManager.getLogger();
  }

  public logUIEvent<T>(
    event: UserBIEvent<T & Partial<ITelemetryContext>>
  ): void {
    let awtEventProperties = this._convertToAWTProperties(
      event.properties as { [key: string]: string }
    );

    // Set user-bi specific columns on telemetry client.
    awtEventProperties.setName(EventType.UserBI);
    awtEventProperties.setType(event.eventName);
    this.awtLogger.logEvent(awtEventProperties);
  }

  public logScenario<T>(
    event: IScenarioEvent<T & Partial<ITelemetryContext>>
  ): void {
    let awtEventProperties = this._convertToAWTProperties(
      event.properties as { [key: string]: string }
    );

    awtEventProperties.setName(EventType.Scenario);
    this.awtLogger.logEvent(awtEventProperties);
  }

  public logPageView(
    pageName: string,
    pageCategory?: string | undefined,
    uri?: string | undefined,
    refUri?: string | undefined,
    properties?: { [key: string]: string } | undefined
  ): void {
    let awtEventProperties = this._convertToAWTProperties(properties);

    this.awtLogger.logPageView(
      pageName,
      pageName,
      pageCategory,
      uri,
      refUri,
      awtEventProperties
    );
  }

  public logException(
    exceptionName: string,
    error: Error,
    properties?: { [key: string]: string } | undefined
  ): void {
    let awtEventProperties = this._convertToAWTProperties(properties);

    this.awtLogger.logFailure(
      exceptionName,
      error
        ? `${error.message}${error.stack ? `\n${error.stack}` : ''}`
        : exceptionName,
      undefined,
      undefined,
      awtEventProperties
    );
  }

  public logTrace(
    message: string,
    logLevel: LogLevel,
    properties?: { [key: string]: string } | undefined
  ): void {
    let awtEventProperties = this._convertToAWTProperties(properties);

    let awtPriority = AWTEventPriority.Normal;
    switch (logLevel) {
      case LogLevel.Verbose:
        awtPriority = AWTEventPriority.Low;
        break;
      case LogLevel.Information:
      case LogLevel.Warning:
        awtPriority = AWTEventPriority.Normal;
        break;
      case LogLevel.Error:
        awtPriority = AWTEventPriority.High;
        break;
      case LogLevel.Critical:
        awtPriority = AWTEventPriority.Immediate_sync;
        break;
      default:
        awtPriority = AWTEventPriority.Normal;
        break;
    }

    awtEventProperties.setName(EventType.Trace);
    awtEventProperties.setEventPriority(awtPriority);
    awtEventProperties.setProperty('LogLevel', logLevel);
    awtEventProperties.setProperty('Message', message);

    this.awtLogger.logEvent(awtEventProperties);
  }

  public flush(callback: () => void): void {
    AWTLogManager.flush(callback);
  }

  private _convertToAWTProperties(
    properties: { [key: string]: string } | undefined
  ): AWTEventProperties {
    let awtEventProperties = new AWTEventProperties();

    if (properties) {
      for (let propertyKey in properties) {
        if (properties.hasOwnProperty(propertyKey)) {
          let propertyValue = properties[propertyKey];
          awtEventProperties.setProperty(
            propertyKey.replace(' ', ''),
            propertyValue
          );
        }
      }
    }

    return awtEventProperties;
  }
}
